<template>
  <div class="tw:flex-col tw:flex tw:w-full tw:mb-8">
    <RateConnectWelcomeBanner v-if="showWelcomeBanner" />
    <RCRunningHeader class="tw:mb-8" />
    <ContainerLayout>
      <router-view />
      <PauseFlowModalWrapper :isOpen="isPauseCampaignOpen" @toggleChangeCampaignModal="openChangeCampaignModal" />
      <ResumeModal
        :isOpen="isResumeCampaignOpen"
        @closeModal="toggleResumeCampaign(isResumeCampaignOpen ? 'close' : 'open')" />
      <ChangeCampaignModal :isOpen="isChangeCampaignOpen" @onCloseClick="closeChangeCampaignModal" />
    </ContainerLayout>
    <ContainerLayout class="tw:flex tw:flex-col tw:items-start tw:justify-start tw:gap-y-2 tw:my-6">
      <p class="tw:text-sm">{{ $t('rc_product_page_disclaimer') }}</p>
      <p class="tw:text-sm">{{ $t('rc_product_page_footnote') }}</p>
    </ContainerLayout>
    <ContainerLayout>
      <RateConnectPlusCrossSellBanner
        v-if="isPerformanceRoute && showInlineBanner"
        :is-price-fetching="isPriceFetching"
        :discounted-monthly-price="discountedMonthlyPrice"
        :monthly-price="monthlyPrice"
        :total-price-before-taxes="totalPriceBeforeTaxes"
        :base-subscription-full-term-price="baseSubscriptionFullTermPrice"
        :plus-upsell-subscribe-action="plusUpsellSubscribeAction"
        @continue="onInlineBannerContinue" />
    </ContainerLayout>
    <RateConnectPlusCrossSellPopup
      v-if="isPerformanceRoute && displayPopup"
      :is-open="isPopupOpen"
      :is-price-fetching="isPriceFetching"
      :discounted-monthly-price="discountedMonthlyPrice"
      :monthly-price="monthlyPrice"
      :total-price-before-taxes="totalPriceBeforeTaxes"
      :base-subscription-full-term-price="baseSubscriptionFullTermPrice"
      :plus-upsell-subscribe-action="plusUpsellSubscribeAction"
      @close="onPopupClose"
      @continue="onPopupContinue"
      @remind-later="onPopupRemindLater" />
    <RateConnectPlusCrossSellSubscriptionModal
      :is-open="isSubscriptionModalOpen"
      @close="onSubscriptionModalClose" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import PauseFlowModalWrapper from '@/components/rateConnect/pauseFlow/PauseFlowModalWrapper.vue'
import ChangeCampaignModal from '@/components/rateConnect/settings/wizards/ChangeCampaignModal.vue'
import RCRunningHeader from '@/components/rateConnect/RCRunningHeader.vue'
import ContainerLayout from '@/layouts/ContainerLayout.vue'
import ResumeModal from '@/components/rateConnect/resumeFlow/ResumeModal.vue'
import RateConnectWelcomeBanner from '@/components/rateConnect/RateConnectWelcomeBanner.vue'
import RateConnectPlusCrossSellBanner from '@/components/rateConnect/studioPlusCrossSell/RateConnectPlusCrossSellBanner.vue'
import RateConnectPlusCrossSellPopup from '@/components/rateConnect/studioPlusCrossSell/RateConnectPlusCrossSellPopup.vue'
import RateConnectPlusCrossSellSubscriptionModal from '@/components/rateConnect/studioPlusCrossSell/RateConnectPlusCrossSellSubscriptionModal.vue'

import { useRateConnect } from '@/components/rateConnect/useRateConnect'
import {
  useRcPlusCrossSell,
  RC_PLUS_CROSS_SELL_INLINE_BANNER_SOURCE,
  RC_PLUS_CROSS_SELL_POPUP_SOURCE,
} from '@/composables/useRcPlusCrossSell'
import { selectedAccommodationId } from '@/layouts/queries'
import { storageSession } from '@/utils/storageService'
import appAnalytics from '@/utils/tracking'

const store = useStore()
const route = useRoute()
const router = useRouter()
const isPerformanceRoute = computed(() => route.name === 'rate-connect-running-performance')

const {
  isPauseCampaignOpen,
  isChangeCampaignOpen,
  closeChangeCampaignModal,
  openChangeCampaignModal,
  toggleResumeCampaign,
  isResumeCampaignOpen,
} = useRateConnect()

const {
  showInlineBanner,
  displayPopup,
  isPopupOpen,
  closePopup,
  dismissPopupRemindMeLater,
  pausePopupForSubscriptionFlow,
  openPopup,
  isPriceFetching,
  discountedMonthlyPrice,
  monthlyPrice,
  totalPriceBeforeTaxes,
  baseSubscriptionFullTermPrice,
  plusUpsellSubscribeAction,
  isSubscriptionModalOpen,
  openSubscriptionModal,
  closeSubscriptionModal,
  subscriptionTrackingSource,
  setSubscriptionTrackingSource,
} = useRcPlusCrossSell()

const onInlineBannerContinue = () => {
  if (plusUpsellSubscribeAction.value === 'login-as-admin') {
    appAnalytics.track(appAnalytics.events.RC_LOGIN_AS_ADMIN_CLICKED, {
      accommodationId: selectedAccommodationId.value,
    })
    store.dispatch('session/logout', '/rate-connect/running/performance?loginAsAdmin=true')
    return
  }

  appAnalytics.track(appAnalytics.events.RC_PLUS_CROSS_SELL_INLINE_CONTINUE_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    source: RC_PLUS_CROSS_SELL_INLINE_BANNER_SOURCE,
  })
  setSubscriptionTrackingSource(RC_PLUS_CROSS_SELL_INLINE_BANNER_SOURCE)
  openSubscriptionModal()
}

const onPopupContinue = () => {
  appAnalytics.track(appAnalytics.events.RC_PLUS_CROSS_SELL_POPUP_CONTINUE_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    source: RC_PLUS_CROSS_SELL_POPUP_SOURCE,
  })

  pausePopupForSubscriptionFlow()

  if (plusUpsellSubscribeAction.value === 'login-as-admin') {
    appAnalytics.track(appAnalytics.events.RC_LOGIN_AS_ADMIN_CLICKED, {
      accommodationId: selectedAccommodationId.value,
    })
    store.dispatch('session/logout', '/rate-connect/running/performance?loginAsAdmin=true')
    return
  }

  setSubscriptionTrackingSource(RC_PLUS_CROSS_SELL_POPUP_SOURCE)
  openSubscriptionModal()
}

const onSubscriptionModalClose = (dismiss = false) => {
  closeSubscriptionModal()

  if (dismiss && subscriptionTrackingSource.value === RC_PLUS_CROSS_SELL_POPUP_SOURCE) {
    openPopup()
  }
}

const onPopupClose = () => {
  appAnalytics.track(appAnalytics.events.RC_PLUS_CROSS_SELL_POPUP_CLOSED, {
    accommodationId: selectedAccommodationId.value,
  })
  closePopup()
}

const onPopupRemindLater = () => {
  appAnalytics.track(appAnalytics.events.RC_PLUS_CROSS_SELL_POPUP_REMIND_LATER_CLICKED, {
    accommodationId: selectedAccommodationId.value,
  })
  dismissPopupRemindMeLater()
}

const showWelcomeBanner: Ref<boolean> = ref(false)
let cleanupListener: (() => void) | null = null

onMounted(async () => {
  if (route.query?.['changeCampaignModal'] === 'true') {
    openChangeCampaignModal()
    await nextTick()
    router.replace({ query: {} })
  }

  const value = storageSession.getItem('new_campaign_created')
  showWelcomeBanner.value = value === 'true'

  cleanupListener = storageSession.onChange('new_campaign_created', (newValue: string | null) => {
    showWelcomeBanner.value = newValue === 'true'
  })
})

onBeforeUnmount(() => {
  if (cleanupListener) {
    cleanupListener()
  }
})
</script>
